import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import api from '../../utils/axios';
import { 
  RefreshCw, 
  CheckCircle, 
  AlertTriangle, 
  Users, 
  Inbox, 
  Trash2, 
  FileText,
  Search,
  Phone,
  Eye,
  Edit3,
  TrendingUp,
  TrendingDown,
  DownloadCloud,
  Filter as FilterIcon,
  X,
  ChevronDown,
  Briefcase,
  Building
} from 'lucide-react';

const statusOptions = [
  { value: '', label: 'All Status', icon: Inbox },
  { value: 'pending', label: 'Pending', icon: AlertTriangle },
  { value: 'contacted', label: 'Contacted', icon: Phone },
  { value: 'hired', label: 'Hired', icon: CheckCircle },
  { value: 'not_interested', label: 'Not Interested', icon: X },
];

const AdminHiringRequests = () => {
  const [stats, setStats] = useState(null);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Check admin session
  useEffect(() => {
    api.get('/hiring-requests', { params: { limit: 1 } })
      .then(() => {})
      .catch(() => window.location.href = '/admin/login');
  }, []);

  // Fetch stats and hiring requests
  useEffect(() => {
    setLoading(true);
    setError('');
    const fetchStats = api.get('/hiring-requests/statistics');
const fetchRequests = statusFilter
? api.get(`/hiring-requests/status/${statusFilter}`, { params: { page } })
: api.get('/hiring-requests', { params: { page } });
    Promise.all([fetchStats, fetchRequests])
      .then(([statsRes, requestsRes]) => {
              setStats(statsRes.data.data);
        setRequests(requestsRes.data.data.hiringRequests || []);
        setTotalPages(requestsRes.data.data.totalPages || 1);
      })
      .catch((err) => {
        setError('Failed to load data. Please try again.');
        setRequests([]);
      })
      .finally(() => setLoading(false));
  }, [statusFilter, page]);

  const handleStatusChange = (id, newStatus) => {
    api.patch(`/hiring-requests/${id}/status`, { status: newStatus })
      .then(() => {
        setRequests(requests => requests.map(r => r.id === id ? { ...r, status: newStatus } : r));
      })
      .catch(() => setError('Failed to update status.'));
  };

  const handleView = async (id) => {
    try {
      const response = await api.get(`/hiring-requests/${id}`);
      setSelectedRequest(response.data.data);
      setShowViewModal(true);
    } catch (err) {
      setError('Failed to load hiring request details.');
    }
  };

  const handleEdit = async (id) => {
    try {
      const response = await api.get(`/hiring-requests/${id}`);
      setSelectedRequest(response.data.data);
      setShowEditModal(true);
    } catch (err) {
      setError('Failed to load hiring request details.');
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedRequest) return;
    
    try {
      await api.put(`/hiring-requests/${selectedRequest.id}`, {
        companyName: selectedRequest.company_name,
        contactPerson: selectedRequest.contact_person,
        email: selectedRequest.email,
        phone: selectedRequest.phone,
        roleType: selectedRequest.role_type,
        experience: selectedRequest.experience,
        positions: selectedRequest.positions,
        urgency: selectedRequest.urgency,
        requirements: selectedRequest.requirements,
        status: selectedRequest.status
      });

      // Update the local state
      setRequests(requests => requests.map(r => 
        r.id === selectedRequest.id ? selectedRequest : r
      ));
      
      setShowEditModal(false);
      setSelectedRequest(null);
    } catch (err) {
      setError('Failed to update hiring request.');
    }
  };

  const handleDelete = (id) => {
    if (!window.confirm('Are you sure you want to delete this hiring request?')) return;
    setDeleting(true);
    api.delete(`/hiring-requests/${id}`)
      .then(() => {
        setRequests(requests => requests.filter(r => r.id !== id));
      })
      .catch(() => setError('Failed to delete hiring request.'))
      .finally(() => setDeleting(false));
  };

  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const response = await api.get('/hiring-requests/export', { 
        params: { format, status: statusFilter },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `hiring-requests-${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      setError('Failed to export data.');
    } finally {
      setExporting(false);
    }
  };

  const filteredRequests = requests.filter(req => 
    req.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.contact_person?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    req.phone?.includes(searchTerm) ||
    req.role_type?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const StatCard = ({ title, value, icon: Icon, color, trend, trendValue }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-soft border border-gray-100 dark:border-gray-700">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{title}</p>
          <p className="text-lg font-bold text-gray-900 dark:text-white">{value}</p>
          <div className="flex items-center mt-1">
            {trend === 'up' ? (
              <TrendingUp className="w-3 h-3 text-green-500 mr-1" />
            ) : (
              <TrendingDown className="w-3 h-3 text-red-500 mr-1" />
            )}
            <span className={`text-xs font-medium ${trend === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trendValue}%
            </span>
          </div>
        </div>
        <div className={`p-2 rounded-lg ${color.icon} bg-opacity-10`}>
          <Icon className="w-4 h-4 text-white" />
        </div>
      </div>
    </div>
  );

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4 gap-3 mt-1">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-1">Hiring Requests</h1>
          <p className="text-xs text-gray-600 dark:text-gray-400">Manage hiring requests and monitor recruitment performance</p>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => handleExport('csv')}
            disabled={exporting}
            className="btn btn-primary-gradient btn-sm flex items-center px-3 py-2 text-xs"
          >
            <DownloadCloud className="w-3 h-3 mr-1" />
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          
          <button
            onClick={() => handleExport('excel')}
            disabled={exporting}
            className="btn btn-outline btn-sm flex items-center px-3 py-2 text-xs"
          >
            <FileText className="w-3 h-3 mr-1" />
            Export Excel
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 px-3 py-2 rounded-lg text-center text-xs border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800 mb-4">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <RefreshCw className="w-8 h-8 animate-spin mb-3 text-primary-600" />
          <p className="text-sm text-gray-700 dark:text-gray-300">Loading hiring requests...</p>
        </div>
      ) : (
        <>
          {/* Stats Grid */}
          {stats && (
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              <StatCard
                title="Total Requests"
                value={stats.total || 0}
                icon={Building}
                color={{ icon: 'bg-blue-500' }}
                trend="up"
                trendValue={12}
              />
              <StatCard
                title="Pending"
                value={stats.pending || 0}
                icon={AlertTriangle}
                color={{ icon: 'bg-yellow-500' }}
                trend="down"
                trendValue={5}
              />
              <StatCard
                title="Contacted"
                value={stats.contacted || 0}
                icon={Phone}
                color={{ icon: 'bg-green-500' }}
                trend="up"
                trendValue={8}
              />
              <StatCard
                title="Hired"
                value={stats.hired || 0}
                icon={CheckCircle}
                color={{ icon: 'bg-purple-500' }}
                trend="up"
                trendValue={15}
              />
            </div>
          )}

          {/* Filters and Search */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-3 shadow-soft border border-gray-100 dark:border-gray-700 mb-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                {/* Search */}
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search hiring requests..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>

                {/* Status Filter */}
                <div className="relative">
                  <select
                    className="appearance-none pl-3 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white cursor-pointer text-sm"
                    value={statusFilter}
                    onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  >
                    {statusOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="btn btn-outline btn-sm flex items-center text-xs"
                >
                  <FilterIcon className="w-3 h-3 mr-1" />
                  Filters
                </button>
                
                <div className="text-xs text-gray-500">
                  Page {page} of {totalPages}
                </div>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Date Range</label>
                    <input
                      type="date"
                      className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-xs"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Role Type</label>
                    <select className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-xs">
                      <option value="">All Roles</option>
                      <option value="frontend">Frontend</option>
                      <option value="backend">Backend</option>
                      <option value="fullstack">Full Stack</option>
                      <option value="mobile">Mobile</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Sort By</label>
                    <select className="w-full px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-xs">
                      <option value="date">Date</option>
                      <option value="company">Company</option>
                      <option value="status">Status</option>
                    </select>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Table Section */}
          <div className="overflow-x-auto w-full rounded-lg shadow border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs" style={{ minWidth: '1000px' }}>
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[120px]">Company</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[150px]">Contact</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[140px]">Role & Experience</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[100px]">Status</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[80px]">Date</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[80px]">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                {filteredRequests.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-3 py-6 text-center text-gray-500 dark:text-gray-400 text-xs">
                      No hiring requests found.
                    </td>
                  </tr>
                ) : (
                  filteredRequests.map((req) => (
                    <tr key={req.id} className="hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors">
                      <td className="px-3 py-2 font-medium text-gray-900 dark:text-white whitespace-nowrap min-w-[120px]">
                        <div className="truncate">{req.company_name}</div>
                        <div className="text-gray-500 text-xs">{req.positions} positions</div>
                      </td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[150px]">
                        <div className="truncate font-medium">{req.contact_person}</div>
                        <div className="truncate text-xs">{req.email}</div>
                        <div className="truncate text-xs">{req.phone}</div>
                      </td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-300 whitespace-nowrap min-w-[140px]">
                        <div className="truncate font-medium">{req.role_type}</div>
                        <div className="truncate text-xs">{req.experience} • {req.urgency}</div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap min-w-[100px]">
                        <select
                          className="rounded-full px-2 py-1 text-xs font-medium focus:ring-2 focus:ring-primary-500 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 text-gray-800 dark:text-gray-200 w-full"
                          value={req.status || 'pending'}
                          onChange={e => handleStatusChange(req.id, e.target.value)}
                        >
                          <option value="pending">Pending</option>
                          <option value="contacted">Contacted</option>
                          <option value="hired">Hired</option>
                          <option value="not_interested">Not Interested</option>
                        </select>
                      </td>
                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap min-w-[80px] text-xs">
                        {new Date(req.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap min-w-[80px]">
                        <div className="flex items-center space-x-1">
                          <button
                            className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                            title="View Details"
                            onClick={() => handleView(req.id)}
                          >
                            <Eye className="w-3 h-3" />
                          </button>
                          <button
                            className="p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                            title="Edit"
                            onClick={() => handleEdit(req.id)}
                          >
                            <Edit3 className="w-3 h-3" />
                          </button>
                          <button
                            className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                            onClick={() => handleDelete(req.id)}
                            disabled={deleting}
                            title="Delete"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <div className="text-xs text-gray-700 dark:text-gray-300">
                  Showing page {page} of {totalPages}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    className="px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* View Modal */}
      {showViewModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Hiring Request Details</h2>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company Name</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedRequest.company_name}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Person</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedRequest.contact_person}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedRequest.email}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedRequest.phone}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role Type</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedRequest.role_type}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Experience Level</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedRequest.experience}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Number of Positions</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedRequest.positions}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Urgency</label>
                    <p className="text-sm text-gray-900 dark:text-white">{selectedRequest.urgency}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <p className="text-sm text-gray-900 dark:text-white capitalize">{selectedRequest.status || 'pending'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Created Date</label>
                    <p className="text-sm text-gray-900 dark:text-white">{new Date(selectedRequest.created_at).toLocaleString()}</p>
                  </div>
                </div>
                
                {selectedRequest.requirements && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Additional Requirements</label>
                    <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{selectedRequest.requirements}</p>
                  </div>
                )}
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowViewModal(false)}
                  className="btn btn-outline btn-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Edit Hiring Request</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Company Name</label>
                    <input
                      type="text"
                      value={selectedRequest.company_name || ''}
                      onChange={(e) => setSelectedRequest({...selectedRequest, company_name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Contact Person</label>
                    <input
                      type="text"
                      value={selectedRequest.contact_person || ''}
                      onChange={(e) => setSelectedRequest({...selectedRequest, contact_person: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Email</label>
                    <input
                      type="email"
                      value={selectedRequest.email || ''}
                      onChange={(e) => setSelectedRequest({...selectedRequest, email: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Phone</label>
                    <input
                      type="tel"
                      value={selectedRequest.phone || ''}
                      onChange={(e) => setSelectedRequest({...selectedRequest, phone: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Role Type</label>
                    <select
                      value={selectedRequest.role_type || ''}
                      onChange={(e) => setSelectedRequest({...selectedRequest, role_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                    >
                      <option value="">Select Role Type</option>
                      <option value="fullstack">Full Stack Developer</option>
                      <option value="frontend">Frontend Developer</option>
                      <option value="backend">Backend Developer</option>
                      <option value="mobile">Mobile Developer</option>
                      <option value="devops">DevOps Engineer</option>
                      <option value="data">Data Scientist</option>
                      <option value="ai">AI/ML Engineer</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Experience Level</label>
                    <select
                      value={selectedRequest.experience || ''}
                      onChange={(e) => setSelectedRequest({...selectedRequest, experience: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                    >
                      <option value="">Select Experience</option>
                      <option value="fresher">Fresher (0-1 years)</option>
                      <option value="junior">Junior (1-3 years)</option>
                      <option value="mid">Mid-level (3-5 years)</option>
                      <option value="senior">Senior (5+ years)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Number of Positions</label>
                    <input
                      type="number"
                      value={selectedRequest.positions || ''}
                      onChange={(e) => setSelectedRequest({...selectedRequest, positions: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Urgency</label>
                    <select
                      value={selectedRequest.urgency || ''}
                      onChange={(e) => setSelectedRequest({...selectedRequest, urgency: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                    >
                      <option value="">Select Urgency</option>
                      <option value="urgent">Urgent (Within 1 week)</option>
                      <option value="high">High (Within 2 weeks)</option>
                      <option value="medium">Medium (Within 1 month)</option>
                      <option value="low">Low (2+ months)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                    <select
                      value={selectedRequest.status || 'pending'}
                      onChange={(e) => setSelectedRequest({...selectedRequest, status: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                    >
                      <option value="pending">Pending</option>
                      <option value="contacted">Contacted</option>
                      <option value="hired">Hired</option>
                      <option value="not_interested">Not Interested</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Additional Requirements</label>
                  <textarea
                    value={selectedRequest.requirements || ''}
                    onChange={(e) => setSelectedRequest({...selectedRequest, requirements: e.target.value})}
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 dark:text-white text-sm"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="btn btn-outline btn-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveEdit}
                  className="btn btn-primary btn-sm"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminHiringRequests; 